import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { checkoutInfoGridProps, checkoutInfoProps, checkoutSubtitleProps } from "@store/theme/props/checkout";
import getWidgetComponent from "@store/utilities/getWidgetComponent";
import React, { FC, useContext } from "react";

const RequestedDate = getWidgetComponent("OrderDetails/OrderDetailsRequestedDate");
const ShippingAddress = getWidgetComponent("OrderDetails/OrderDetailsShippingAddress");

interface Props {}

export interface StoreOrderShippingInformationStyles {
    title?: TypographyProps;
    gridContainer?: GridContainerProps;
    addressGridItem?: GridItemProps;
    infoGridItem?: GridItemProps;
    infoGridContainer?: GridContainerProps;
    methodGridItem?: GridItemProps;
    dateRequestedGridItem?: GridItemProps;
    trackingGridItem?: GridItemProps;
    infoLabel?: TypographyPresentationProps;
    infoValue?: TypographyPresentationProps;
}

export const storeOrderShippingInformationStyles: StoreOrderShippingInformationStyles = {
    title: checkoutSubtitleProps,
    gridContainer: checkoutInfoGridProps.container,
    addressGridItem: checkoutInfoGridProps.item,
    infoGridItem: checkoutInfoGridProps.largeItem,
    infoGridContainer: checkoutInfoGridProps.container,
    methodGridItem: checkoutInfoGridProps.largeSubItem,
    dateRequestedGridItem: checkoutInfoGridProps.largeSubItem,
    trackingGridItem: checkoutInfoGridProps.largeSubItem,
    infoLabel: checkoutInfoProps.label,
    infoValue: checkoutInfoProps.value,
};

const styles = storeOrderShippingInformationStyles;

const StoreOrderShippingInformation: FC<Props> = () => {
    const { value: order } = useContext(OrderStateContext);

    if (!order) {
        return null;
    }

    const carrierName = order.properties?.carrierName;
    const trackingNumber = order.properties?.trackingNumber;
    const trackingUrl = order.properties?.trackingUrl;

    const hasTrackingUrl = !!trackingUrl;
    const trackingLabel = hasTrackingUrl ? "Tracking url" : "Tracking Number(s)";

    return (
        <>
            <Typography {...styles.title}>{translate("Shipping Information")}</Typography>
            <GridContainer {...styles.gridContainer}>
                <GridItem {...styles.addressGridItem}>
                    <ShippingAddress />
                </GridItem>
                <GridItem {...styles.infoGridItem}>
                    <GridContainer {...styles.infoGridContainer}>
                        {order.requestedDeliveryDateDisplay ? (
                            <GridItem {...styles.dateRequestedGridItem}>
                                <RequestedDate />
                            </GridItem>
                        ) : null}
                        {(trackingNumber || carrierName) && (
                            <>
                                <GridItem {...styles.trackingGridItem}>
                                    {carrierName && (
                                        <>
                                            <Typography {...styles.infoLabel}>{translate("Carrier")}</Typography>
                                            <Typography {...styles.infoValue}>{carrierName}</Typography>
                                        </>
                                    )}
                                    {trackingNumber && (
                                        <>
                                            <Typography {...styles.infoLabel}>{translate(trackingLabel)}</Typography>
                                            <Typography {...styles.infoValue}>
                                                {hasTrackingUrl ? (
                                                    <Link href={trackingUrl}>{trackingNumber}</Link>
                                                ) : (
                                                    <span>{trackingNumber}</span>
                                                )}
                                            </Typography>
                                        </>
                                    )}
                                </GridItem>
                            </>
                        )}
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </>
    );
};

export default StoreOrderShippingInformation;
